import React, { FormEvent, useEffect, useState } from 'react'
import { Form } from 'react-router-dom';
import { Alert, Button, IconButton, MenuItem, Select, TextField } from '@mui/material';

import { ICustomerLight } from '../../types/customer';
import { IVendorCustomer } from '../../types/vendor';

import { ErrorProvider } from '../../classes/errorProvider';
import { useListContext } from '../../contexts/listContext';
import { VendorService } from '../../services/vendorService';
import { CustomerService } from '../../services/customerService';
import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';
import Spinner from '../spinner';

import '../../styles/vendorCustomerForm.scss';
import NumberField from '../shared/numberField';

export interface IVendorCustomersFormProps {
	id: string;
	customers: IVendorCustomer[];
}

const VendorCustomersForm: React.FunctionComponent<IVendorCustomersFormProps> = ({ id, customers }) => {
	const listContext = useListContext();
	const [vendorCustomers, setVendorCustomers] = useState<IVendorCustomer[]>([...customers ?? []].sort((a, b) => UtilityService.compareStrings(a.customerName, b.customerName)));
	const [activeCustomers, setActiveCustomers] = useState<ICustomerLight[]>([]);
	const [unassignedCustomers, setUnassignedCustomers] = useState<ICustomerLight[]>([]);
	const [customerIdField, setCustomerIdField] = useState("");
	const [distance, setDistance] = useState(0);
	const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
	const [isAdding, setIsAdding] = useState(false);
	const [isRemoving, setIsRemoving] = useState(false);
	const [isRemovingCustomerId, setIsRemovingCustomerId] = useState("");
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [invalidCustomerSelectionReason, setInvalidCustomerSelectionReason] = useState<string | JSX.Element | undefined>();

	const michelinBrandId = listContext.brands?.find(b => b.name?.toLowerCase() === "michelin")?.value;

	const loadCustomers = async () => {
		setIsLoadingCustomers(true);

		const tempCustomers: ICustomerLight[] = (await CustomerService.getActiveCustomers())?.data ?? [];
		setActiveCustomers([...tempCustomers]);

		const unassigned: ICustomerLight[] = tempCustomers.filter((t: ICustomerLight) => {
			let isUnassigned = true;

			if (vendorCustomers) {
				vendorCustomers.map((vc) => {
					if (vc.customerId === +t.id) isUnassigned = false;
				})
			}

			return isUnassigned;
		}).slice();

		unassigned.sort((a, b) => UtilityService.compareStrings(a.name, b.name))

		setUnassignedCustomers(unassigned);
		setCustomerIdField(unassigned[0].id ? unassigned[0].id : "");
		setIsLoadingCustomers(false);
	}

	useEffect(() => {
		syncCustomersWithVendor();
		loadCustomers();
	}, [vendorCustomers])

	const isSelectedCustomerMichelinBrand = () => {
		var customerBrandId = activeCustomers?.find(ac => ac.id == customerIdField)?.brandId;
		return customerBrandId && customerBrandId == michelinBrandId;
	}

	const handleGrantAccess = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setIsAdding(true);

		if (customerIdField && customerIdField != "") {
			const request: IVendorCustomer = {
				vendorId: +id,
				customerId: +customerIdField,
				distance: distance
			};

			VendorService.addCustomer(request)
				.then((response) => {
					console.log('VendorService.addCustomer: SUCCESS', response);

					let vc: IVendorCustomer = {
						customerId: response.data.customerId,
						customerBrandId: response.data.customerBrandId,
						customerName: response.data.customerName,
						customerState: response.data.customerState,
						distance: response.data.distance,
						id: response.data.id,
						vendorId: response.data.vendorId
					}

					var newUnassignedCustomers = unassignedCustomers.filter((item) => { return item.id != customerIdField });

					setVendorCustomers(vendorCustomers => [...vendorCustomers, vc].sort((a, b) => UtilityService.compareStrings(a.customerName, b.customerName)));
					setUnassignedCustomers(newUnassignedCustomers);
					setCustomerIdField(unassignedCustomers[0].id ? newUnassignedCustomers[0].id : "");
					setDistance(0);
				})
				.catch((error) => {
					console.log('VendorService.addCustomer: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsAdding(false);
				});
		}
	}

	const handleRevokeAccess = async (customerId: string) => {
		setErrorMessage(undefined);
		setIsRemoving(true);
		setIsRemovingCustomerId(customerId);
		if (customerId) {
			VendorService.removeCustomer(id, customerId)
				.then((response) => {
					console.log('VendorService.removeCustomer: SUCCESS', response);

					const removedCustomer = activeCustomers.find(c => c.id == customerId);
					const newUnassignedCustomers = [...unassignedCustomers, removedCustomer];

					setUnassignedCustomers(newUnassignedCustomers.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));
					setVendorCustomers(vendorCustomers.filter((item) => { return item.customerId.toString() != customerId }).sort((a, b) => UtilityService.compareStrings(a.customerName, b.customerName)));
					setCustomerIdField(newUnassignedCustomers[0].id);
				})
				.catch((error) => {
					console.log('VendorService.removeCustomer: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsRemoving(false);
					setIsRemovingCustomerId("");
				});
		}
	}

	// Sync the vendor's referenced customers array passed in from the parent page with changes made here
	const syncCustomersWithVendor = () => {
		customers.splice(0);
		customers.push(...vendorCustomers);
	}

	return (
		<section className="hwi-vendor-customer-form">
			<Spinner dependency={isLoadingCustomers} text='Loading customers...'>
				<div className="facility-table">
					<div className="headers">
						<div className='customer-container-col'>
							<div className='customer-col'>Customer</div>
							<div className='state-col'>State</div>
						</div>
						<div className='distance-col'>Distance</div>
						<div className='actions-col'></div>
					</div>
					<div className="body">
						{vendorCustomers.length > 0 ? (
							<>
								{vendorCustomers.map((item, index) => (
									<div className="row" key={index}>
										<div className='customer-container-col'>
											<div className='customer-col'>{item.customerName}</div>
											<div className='state-col'>{item.customerState}</div>
										</div>
										<div className='distance-col'>{item.distance} miles</div>
										<div className='actions-col'>
											<Button onClick={() => handleRevokeAccess(item.customerId.toString())} disabled={isRemoving}>{isRemoving && isRemovingCustomerId == item.customerId.toString() ? "Removing..." : (<><Icons.RemoveItem fill="#a82525" height="16px"></Icons.RemoveItem><span>&nbsp;Remove</span></>)}</Button>
										</div>
									</div>
								))}
							</>
						) : (
							<div className="no-results">0 results</div>
						)}

					</div>
				</div>
				{errorMessage &&
					<Alert className="alert-error" severity="error" action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>{errorMessage}</Alert>
				}
				{invalidCustomerSelectionReason &&
					<Alert className="alert-error" severity="error">{invalidCustomerSelectionReason}</Alert>
				}
				<Form onSubmit={(event) => handleGrantAccess(event)}>
					<div className="row">
						<div className="customer-field">
							<Select
								required
								className="status-field"
								value={customerIdField}
								fullWidth
								onChange={(event) => setCustomerIdField(event.target.value)}
							>
								{unassignedCustomers.map((item, index) => (
									<MenuItem value={item.id} key={index} >{item.name}</MenuItem>
								))}
							</Select>

						</div>
						<div className="distance-field-container">
							<NumberField className="distance-field"
								name="distance"
								value={distance}
								min={0}
								step={1}
								required
								type="number"
								onChange={(event) => { setDistance(parseInt(event.target.value)) }}
								onFocus={(event) => { event.target.select() }}
							></NumberField>
							<div className="distance-field-label">
								{" "}miles
							</div>
						</div>
						<div className="controls">
							<Button type="submit" variant="text" className="save-button" disabled={isAdding || !!invalidCustomerSelectionReason}>{isAdding ? "Adding..." : (<><Icons.AddItem fill="#272f50" height="16px"></Icons.AddItem><span>&nbsp;Add</span></>)}</Button>
						</div>
					</div>
				</Form>
			</Spinner>
		</section>
	)
}

export default VendorCustomersForm;